import { styled, makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { getOfferingCardTagChipStyle } from '@components/pga-coach-offering-card/offering-card-tag.styles'
import {
  coachProfileBandOuter,
  coachProfileBandSectionGap,
  coachProfileBookingCardChrome,
  coachProfileContentCanvas,
  coachProfileInlineTextLink,
  coachProfileFacilityMetaLink,
  coachProfileMetaText,
  coachProfileRowInlineMeta,
  coachProfileRowLocationMeta,
  coachProfileRowMetaEllipsisText,
  coachProfilePillCtaPrimary,
  coachProfilePillCtaSecondary,
  coachProfileSectionHeading,
} from '@lib/pga-coach/coach-profile-layout'

const PROFILE_FACILITY_CARD_RADIUS = 20

/** Profile UX accordion header — same minHeight collapsed and expanded to avoid MUI toggle jump. */
export const PROFILE_FACILITY_ACCORDION_SUMMARY_MIN_HEIGHT = 56
/** `theme.spacing` units for summary content vertical margin (profile UX only). */
export const PROFILE_FACILITY_ACCORDION_SUMMARY_VERTICAL_PADDING = 1.5

export const Root = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  color: theme.palette.text.primary,
  ...coachProfileBandOuter(theme),
}))

export const FacilitiesWrapper = styled(Box)(() => ({
  display: 'block',
  width: '100%',
}))

export const OfferingsWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  [theme.breakpoints.up('sm')]: {
    '& div:nth-child(odd)': {
      marginRight: '2rem',
    },
  },
}))

export const Offering = styled(Box)(({ theme }) => ({
  width: 'calc(50% - 1rem)',

  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}))

export const FlexRow = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
}))

export const BoldText = styled(Typography)(() => ({
  display: 'flex',
  fontWeight: '600',
}))

export const LightText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(3),
  fontSize: '1rem',
  color: theme.palette.grey.dark,
}))

export const useStyles = makeStyles((theme) => ({
  root: ({ inColumn }) => ({
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    ...coachProfileBandOuter(theme, {
      inColumn,
      fullBleed: !inColumn,
      ...(inColumn ? coachProfileBandSectionGap(theme) : {}),
      ...(inColumn ? { paddingBottom: 0 } : {}),
    }),
  }),
  /**
   * Inner canvas. Horizontal gutters live on `root` when this band is
   * full-bleed; in the marketplace content column the parent 1200px
   * shell already padded the row.
   */
  insetContainer: ({ inColumn }) => ({
    ...coachProfileContentCanvas({ inColumn }),
    paddingTop: inColumn ? 0 : theme.spacing(5),
    paddingBottom: inColumn ? 0 : theme.spacing(5),
    [theme.breakpoints.up('md')]: {
      paddingTop: inColumn ? 0 : theme.spacing(6),
      paddingBottom: inColumn ? 0 : theme.spacing(6),
    },
  }),
  /**
   * Section headline aligned with the canonical site pattern
   * (`pga-coach-journey.sectionHeading`, `coach-photos`): h4 variant + fontWeight 800 + navy.
   */
  sectionHeading: ({ inColumn }) => ({
    ...(inColumn ? coachProfileSectionHeading(theme) : {
      fontWeight: 800,
      color: theme.palette.secondary.dark,
    }),
    margin: 0,
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(4),
    },
  }),
  link: ({ inColumn }) => (inColumn ? coachProfileInlineTextLink(theme) : {
    /**
     * Match the brand-navy text-link color (`secondary.main`) used
     * across coach-profile interactive text affordances (bio "Read
     * More" toggle, hero "Browse Coaches" link), so the website link
     * in the facility detail row reads as the same interactive-text
     * affordance used elsewhere on this profile. Hover/focus shifts
     * to `secondary.dark` and
     * solidifies the underline.
     */
    color: theme.palette.secondary.main,
    fontWeight: 600,
    textDecoration: 'none',
    borderBottom: `1px solid ${theme.palette.grey.light}`,
    transition: 'border-color 150ms ease, color 150ms ease',
    '&:hover, &:focus-visible': {
      color: theme.palette.secondary.dark,
      borderBottomColor: theme.palette.secondary.dark,
    },
  }),
  singleFacilityCard: {
    marginTop: theme.spacing(2),
  },
  facilityHeadline: {
    fontFamily: theme.typography.h3.fontFamily,
    fontSize: '1.0625rem',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    lineHeight: 1.35,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      fontSize: '1.125rem',
    },
  },
  facilityPhoto: {
    width: '100%',
    maxHeight: 280,
    objectFit: 'cover',
    borderRadius: 10,
    marginBottom: theme.spacing(2),
    display: 'block',
  },
  facilityDescription: {
    fontSize: '0.9375rem',
    lineHeight: 1.55,
    color: theme.palette.grey.darker,
    marginBottom: theme.spacing(2),
  },
  publicCoachingNote: ({ inColumn }) => (inColumn ? {} : {
    /**
     * Legacy full-bleed profile: soft tip callout in accordion body.
     */
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    margin: 0,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 12,
    backgroundColor: theme.palette.blue.light100,
    border: '1px solid rgba(50, 104, 167, 0.22)',
  }),
  /**
   * Flagged profile: compact pill beside the accordion chevron (shop tag chips).
   */
  publicCoachingNoteSummary: ({ inColumn }) => (inColumn ? {
    ...getOfferingCardTagChipStyle(theme),
    flexShrink: 0,
    marginLeft: theme.spacing(1.5),
    marginRight: theme.spacing(1.5),
    '& .MuiTypography-root': {
      color: 'inherit',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      lineHeight: 'inherit',
    },
  } : {}),
  publicCoachingNoteIcon: {
    flexShrink: 0,
    fontSize: 18,
    color: theme.palette.blue.main,
  },
  publicCoachingNoteText: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.4,
    color: theme.palette.secondary.dark,
  },
  /**
   * Profile UX mobile accordion body: quiet non-link meta (spacing matches
   * address row; no blue/bold location link styling).
   */
  publicCoachingNoteMetaRow: ({ inColumn }) => (inColumn ? {
    ...coachProfileRowInlineMeta(theme),
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular,
    '& svg': {
      width: 16,
      height: 16,
      flexShrink: 0,
      color: theme.palette.text.secondary,
    },
  } : {}),
  publicCoachingNoteMetaText: ({ inColumn }) => (inColumn ? {
    color: 'inherit',
    fontWeight: 400,
    fontSize: theme.typography.subtitle2.fontSize,
    lineHeight: theme.typography.subtitle2.lineHeight,
    letterSpacing: theme.typography.subtitle2.letterSpacing,
    fontFamily: theme.typography.body2.fontFamily,
    minWidth: 0,
  } : {}),
  detailRow: ({ inColumn }) => (inColumn ? {
    ...coachProfileRowInlineMeta(theme),
    ...coachProfileRowLocationMeta(theme),
    marginBottom: theme.spacing(1),
  } : {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.25),
  }),
  websiteRow: ({ inColumn }) => (inColumn ? {
    ...coachProfileRowInlineMeta(theme),
    ...coachProfileRowLocationMeta(theme),
    marginBottom: theme.spacing(1),
  } : {}),
  facilityMetaText: ({ inColumn }) => (inColumn ? coachProfileRowMetaEllipsisText(theme) : {}),
  facilityMetaLink: ({ inColumn }) => (inColumn ? coachProfileFacilityMetaLink(theme) : {}),
  iconWell: {
    /**
     * Match `coach-bio.bioStatIconWell` (gold disc + dark glyph)
     * so the facility detail-row icons read as the same visual badge
     * the bio stat row uses on >= sm. Same 40px circle, same gold
     * fill, same dark navy icon, same `fontSize: 20` glyph.
     */
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.gold.main,
    color: theme.palette.secondary.dark,
    '& .MuiSvgIcon-root': {
      fontSize: 20,
    },
  },
  detailText: ({ inColumn }) => (inColumn ? {
    ...coachProfileMetaText(theme),
    margin: 0,
    paddingTop: 0,
  } : {
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.45,
    letterSpacing: '-0.01em',
    color: theme.palette.grey.darkest,
    paddingTop: 8,
    minWidth: 0,
  }),
  mapCard: ({ inColumn }) => {
    const cardRadius = inColumn ? PROFILE_FACILITY_CARD_RADIUS : 24
    return {
      width: '100%',
      marginTop: theme.spacing(2),
      borderRadius: cardRadius,
      overflow: 'hidden',
      backgroundColor: '#e8ecf1',
      ...(inColumn
        ? {
          border: coachProfileBookingCardChrome(theme).border,
          boxShadow: 'none',
        }
        : {
          border: '1px solid rgba(50, 104, 167, 0.22)',
          boxShadow: 'none',
        }),
    }
  },
  mapWrap: ({ inColumn }) => ({
    position: 'relative',
    width: '100%',
    lineHeight: 0,
    ...(inColumn ? {
      outline: 'none',
      '&:focus, &:focus-within': {
        outline: 'none',
      },
      '& > div:focus, & > div:focus-visible': {
        outline: 'none',
      },
    } : {}),
    '& .gm-ui-hover-effect': {
      width: '28px !important',
      height: '28px !important',
      top: '8px !important',
      right: '8px !important',
      display: 'flex !important',
      alignItems: 'center !important',
      justifyContent: 'center !important',
    },
    '& .gm-ui-hover-effect > span': {
      width: '18px !important',
      height: '18px !important',
      margin: '5px !important',
    },
    '& .gm-style-iw-chr': {
      height: 0,
      minHeight: 0,
      padding: 0,
      margin: 0,
    },
    '& .gm-style-iw-c': {
      padding: '0 !important',
      border: 'none !important',
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: '0 12px 28px rgba(15, 34, 52, 0.2)',
      boxSizing: 'border-box !important',
      maxWidth: '280px !important',
    },
    '& .gm-style-iw-d': {
      overflow: 'visible !important',
      border: 'none !important',
      boxSizing: 'border-box !important',
      maxWidth: '280px !important',
    },
    '& .gm-style-iw-tc': {
      filter: 'drop-shadow(0 2px 4px rgba(15, 34, 52, 0.12))',
    },
  }),
  mapSurface: ({ inColumn }) => {
    const topRadius = inColumn ? PROFILE_FACILITY_CARD_RADIUS : 24
    return {
      width: '100%',
      height: 360,
      borderRadius: `${topRadius}px ${topRadius}px 0 0`,
      overflow: 'hidden',
      [theme.breakpoints.up('md')]: {
        height: 420,
      },
    }
  },
  mapLoading: ({ inColumn }) => {
    const topRadius = inColumn ? PROFILE_FACILITY_CARD_RADIUS : 24
    return {
      minHeight: 360,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.palette.text.secondary,
      fontSize: '0.9375rem',
      borderRadius: `${topRadius}px ${topRadius}px 0 0`,
      overflow: 'hidden',
      [theme.breakpoints.up('md')]: {
        minHeight: 420,
      },
    }
  },
  mapActions: ({ inColumn }) => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    borderTop: `1px solid ${theme.palette.divider}`,
    ...(inColumn ? {
      flexDirection: 'column',
      flexWrap: 'nowrap',
      padding: theme.spacing(1),
      justifyContent: 'flex-start',
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        flexWrap: 'wrap',
      },
      '& > a, & > button': {
        width: '100%',
        flex: '0 0 auto',
        minWidth: 0,
        [theme.breakpoints.up('md')]: {
          flex: '0 0 auto',
          width: 'auto',
        },
      },
    } : {}),
  }),
  /**
   * Compact `ButtonCta` sizing for map action links — slightly
   * smaller than the default 48px height.
   */
  mapActionButton: ({ inColumn }) => (inColumn ? {
    ...coachProfilePillCtaPrimary(theme),
    width: '100%',
    flex: '0 0 auto',
    minWidth: 0,
    [theme.breakpoints.up('md')]: {
      width: 'auto',
      flex: '0 0 auto',
    },
  } : {
    minHeight: 40,
    height: 40,
    fontSize: '0.875rem',
    padding: theme.spacing(0.75, 2.5),
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: 'none',
  }),
  /**
   * Flagged profile: Send a Request–style pill. Legacy: journey empty-state outline.
   */
  mapActionButtonOutlined: ({ inColumn }) => (inColumn ? {
    ...coachProfilePillCtaSecondary(theme),
    width: '100%',
    flex: '0 0 auto',
    minWidth: 0,
    [theme.breakpoints.up('md')]: {
      width: 'auto',
      flex: '0 0 auto',
    },
  } : {
    '&&': {
      color: theme.palette.secondary.light,
      border: `1px solid ${theme.palette.secondary.light}`,
      backgroundColor: '#FAFAFA',
      padding: theme.spacing(0.75, 2.5),
      '&:hover': {
        border: `1px solid ${theme.palette.secondary.light}`,
        backgroundColor: theme.palette.blue.light100,
        color: theme.palette.blue.main,
        boxShadow: 'none',
      },
    },
  }),
  mapInfoWindow: {
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    padding: theme.spacing(2, 2, 1.75),
    lineHeight: 1.4,
    backgroundColor: theme.palette.common.white,
    position: 'relative',
    zIndex: 1,
    pointerEvents: 'auto',
  },
  mapInfoWindowTitle: {
    margin: 0,
    paddingRight: theme.spacing(3),
    fontSize: '0.9375rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.secondary.dark,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  mapInfoWindowActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    position: 'relative',
    zIndex: 1,
    pointerEvents: 'auto',
  },
  mapInfoWindowButton: {
    width: '100%',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  offeringsSectionLabel: {
    /**
     * Canonical coach-profile eyebrow type system: overline font
     * family, 12px, 700, `0.12em` tracking, uppercase. Color stays
     * `grey.dark` because this label sits inside the facility
     * accordion as a quiet section divider rather than a primary
     * eyebrow.
     */
    fontFamily: theme.typography.overline.fontFamily,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: theme.palette.grey.dark,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  offeringTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    marginBottom: theme.spacing(0.5),
  },
  offeringDescription: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.grey.dark,
    marginTop: 0,
    marginBottom: theme.spacing(2),
  },
  facilitiesAccordionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  facilityAccordion: ({ inColumn }) => {
    const cardRadius = inColumn ? PROFILE_FACILITY_CARD_RADIUS : 12
    return {
      ...(inColumn ? {
        ...coachProfileBookingCardChrome(theme),
        // Stacked cards read cleaner with border only (booking card keeps shadow).
        boxShadow: 'none',
      } : {
        border: `1px solid ${theme.palette.grey.light}`,
        boxShadow: 'none',
      }),
      backgroundColor: theme.palette.common.white,
      borderRadius: cardRadius,
      overflow: 'hidden',
      marginTop: inColumn ? 0 : theme.spacing(1.5),
      '&:before': {
        display: 'none',
      },
      /**
       * MUI Accordion adds `.MuiAccordion-rounded` (Paper group styling):
       * `border-radius: 0` with only `:first-child` top / `:last-child`
       * bottom corners. Inside `facilitiesAccordionList` that squares off
       * the first and last stacked cards while middle rows keep our radius.
       */
      '&.MuiAccordion-rounded': {
        borderRadius: cardRadius,
        '&:first-child, &:last-child': {
          borderRadius: cardRadius,
        },
      },
      '&:first-of-type': {
        marginTop: inColumn ? 0 : theme.spacing(2),
      },
      '& .MuiCollapse-root': {
        backgroundColor: theme.palette.common.white,
      },
      '& .MuiAccordionSummary-root': {
        backgroundColor: theme.palette.common.white,
        ...(inColumn ? {
          minHeight: PROFILE_FACILITY_ACCORDION_SUMMARY_MIN_HEIGHT,
          '&.Mui-expanded': {
            minHeight: PROFILE_FACILITY_ACCORDION_SUMMARY_MIN_HEIGHT,
          },
        } : {}),
        '&:hover:not(.Mui-disabled)': {
          backgroundColor: theme.palette.common.white,
        },
        '&:active:not(.Mui-disabled)': {
          backgroundColor: theme.palette.common.white,
        },
        '&.Mui-expanded': {
          backgroundColor: theme.palette.common.white,
        },
        '&.Mui-focused, &.Mui-focusVisible': {
          backgroundColor: theme.palette.common.white,
        },
        ...(inColumn ? {
          '&:focus:not(.Mui-focusVisible):not(:focus-visible)': {
            outline: 'none',
          },
        } : {}),
      },
      '& .MuiAccordionDetails-root': {
        backgroundColor: theme.palette.common.white,
      },
      /**
       * Collapsed: one booking-card shell — full radius on root + summary.
       * (Without `square` on Accordion, MUI's `rounded` class zeros corners
       * on items that are not :first-child/:last-child in the list.)
       */
      '&:not(.Mui-expanded)': {
        '& .MuiAccordionSummary-root': {
          borderRadius: cardRadius,
          ...(inColumn ? {
            '&.Mui-focusVisible, &:active:not(.Mui-disabled)': {
              borderRadius: cardRadius,
            },
          } : {}),
        },
        '& .MuiAccordionDetails-root': {
          borderTop: 'none',
        },
      },
      '&.Mui-expanded': {
        margin: 0,
        '& .MuiAccordionSummary-root': {
          borderTopLeftRadius: cardRadius,
          borderTopRightRadius: cardRadius,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          ...(inColumn ? {
            '&.Mui-focusVisible, &:active:not(.Mui-disabled)': {
              borderTopLeftRadius: cardRadius,
              borderTopRightRadius: cardRadius,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
          } : {}),
        },
        '& .MuiAccordionDetails-root': {
          borderBottomLeftRadius: cardRadius,
          borderBottomRightRadius: cardRadius,
        },
      },
      ...(inColumn ? {
        '&.Mui-focusVisible': {
          outline: 'none',
          boxShadow: 'none',
        },
      } : {}),
    }
  },
  accordionSummary: ({ inColumn }) => {
    const summaryMinHeight = inColumn
      ? PROFILE_FACILITY_ACCORDION_SUMMARY_MIN_HEIGHT
      : 56
    const profileSummaryContentMargin = theme.spacing(
      PROFILE_FACILITY_ACCORDION_SUMMARY_VERTICAL_PADDING,
      0,
    )
    return {
      paddingLeft: theme.spacing(2),
      paddingRight: inColumn ? theme.spacing(2) : theme.spacing(1),
      minHeight: summaryMinHeight,
      /**
       * MUI AccordionSummary defaults to minHeight 48 collapsed and 64 when
       * `.Mui-expanded`, with content margin 12px → 20px. That makes flagged
       * profile headers jump when toggling — worse once the public coaching
       * pill lives in the summary row. Keep one minHeight and symmetric inset.
       */
      ...(inColumn ? {
        '&&.Mui-expanded': {
          minHeight: summaryMinHeight,
        },
        '&:focus': {
          outline: 'none',
        },
        '&.Mui-focusVisible, &:focus-visible': {
          outline: `2px solid ${theme.palette.secondary.main}`,
          outlineOffset: -2,
        },
      } : {}),
      '& .MuiAccordionSummary-content': {
        margin: inColumn ? profileSummaryContentMargin : theme.spacing(1.25, 0),
        alignItems: 'center',
        ...(inColumn ? {
          flex: '1 1 auto',
          minWidth: 0,
        } : {}),
        ...(inColumn ? {
          '&.Mui-expanded, &.MuiAccordionSummary-content.Mui-expanded': {
            margin: profileSummaryContentMargin,
          },
        } : {}),
      },
    }
  },
  accordionSummaryMain: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    gap: 8,
  },
  accordionSummaryTitle: ({ inColumn }) => (inColumn ? {
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: theme.typography.body1.fontFamily,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    letterSpacing: '-0.01em',
    lineHeight: 1.35,
    color: theme.palette.secondary.dark,
  } : {
    fontFamily: theme.typography.h3.fontFamily,
    fontSize: 'clamp(1.1rem, 0.4vw + 1rem, 1.35rem)',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: theme.palette.text.primary,
  }),
  accordionDetails: {
    /**
     * The accordion summary draws a `borderTop` divider here (see
     * below). Without top padding, the first child (`facilityCityLine`
     * "Chicago, IL") sits flush against that divider with only the 1px
     * border for breathing room. Use symmetric `spacing(2)` (16px)
     * vertical+horizontal padding so the city line, photo,
     * "Coaching available to public" pill, address rows, and map all
     * have consistent inset from the accordion edges.
     */
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.grey.light}`,
    backgroundColor: theme.palette.common.white,
  },
  expandIcon: {
    color: theme.palette.grey.dark,
  },
  /**
   * Flagged profile: plain grey chevron — no IconButton well (match booking
   * card minimal affordance; address/website rows use inline offering-card meta).
   */
  accordionExpandIcon: ({ inColumn }) => (inColumn ? {
    flexShrink: 0,
    width: 'auto',
    height: 'auto',
    minWidth: 0,
    padding: 0,
    marginTop: 0,
    marginBottom: 0,
    marginRight: 0,
    marginLeft: theme.spacing(1),
    borderRadius: 0,
    backgroundColor: 'transparent',
    color: theme.palette.grey.dark,
    border: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:active': {
      backgroundColor: 'transparent',
    },
    '&:focus': {
      outline: 'none',
      backgroundColor: 'transparent',
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'transparent',
      outline: 'none',
    },
    '&.Mui-expanded': {
      backgroundColor: 'transparent',
    },
    '& .MuiIconButton-label': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& .MuiSvgIcon-root': {
      fontSize: 20,
      color: 'inherit',
    },
    '& .MuiTouchRipple-root': {
      display: 'none',
    },
  } : {}),
}))
